import React, { useState, useEffect, useMemo, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const MarkdownRenderer = ({ content, increaseLineSpace = false, chunkSize = 1000, renderDelay = 10 }) => {
  // 预处理 Markdown 内容，确保标题格式正确
  const preprocessMarkdown = (text) => {
    if (!text) return '';
    
    // 处理标题格式：确保 # 后面有空格
    // 匹配 #、##、### 等开头但后面没有空格的情况
    return text.replace(/^(#{1,6})([^\s#])/gm, '$1 $2');
  };
  
  // 应用预处理
  const processedContent = preprocessMarkdown(content);

  // 自定义组件渲染
  const components = {
    // 段落样式优化 - 根据 increaseLineSpace 参数决定行间距
    p: ({ node, ...props }) => (
      <p className={`my-2 text-gray-700 ${increaseLineSpace ? 'leading-loose mb-6' : 'leading-relaxed'}`} {...props} />
    ),
    // 链接样式优化 - 显示域名作为链接文本，使用灰色按钮样式
    a: ({ node, href, ...props }) => {
      try {
        // 从URL中提取域名
        const url = new URL(href);
        let domain = url.hostname;
        
        // 移除域名中的 "www." 前缀
        if (domain.startsWith('www.')) {
          domain = domain.substring(4);
        }
        
        return (
          <a 
            href={href} 
            className="inline-block bg-gray-100 text-gray-500 px-3 py-1 rounded-full text-xs hover:bg-gray-200 transition-colors" 
            target="_blank" 
            rel="noopener noreferrer"
          >
            {domain}
          </a>
        );
      } catch (e) {
        // 如果URL解析失败，使用原始链接
        return (
          <a 
            href={href} 
            className="inline-block bg-gray-100 text-gray-500 px-3 py-1 rounded-full text-xs hover:bg-gray-200 transition-colors" 
            target="_blank" 
            rel="noopener noreferrer"
          >
            {href}
          </a>
        );
      }
    },
    // 表格样式优化
    table: ({ node, ...props }) => (
      <div className="overflow-x-auto my-4">
        <table className="min-w-full divide-y divide-gray-300 border border-gray-300" {...props} />
      </div>
    ),
    thead: ({ node, ...props }) => (
      <thead className="bg-gray-100" {...props} />
    ),
    tbody: ({ node, ...props }) => (
      <tbody className="divide-y divide-gray-200 bg-white" {...props} />
    ),
    tr: ({ node, ...props }) => (
      <tr className="hover:bg-gray-50" {...props} />
    ),
    th: ({ node, ...props }) => (
      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700" {...props} />
    ),
    td: ({ node, ...props }) => (
      <td className="px-4 py-3 text-sm text-gray-700" {...props} />
    ),
    // 其他元素样式优化
    h1: ({ node, children, ...props }) => (
      <h1 className="text-2xl font-bold my-4 text-gray-800" {...props}>{children}</h1>
    ),
    h2: ({ node, children, ...props }) => (
      <h2 className="text-xl font-bold my-3 text-gray-800" {...props}>{children}</h2>
    ),
    h3: ({ node, children, ...props }) => (
      <h3 className="text-lg font-bold my-2 text-gray-800" {...props}>{children}</h3>
    ),
    h4: ({ node, children, ...props }) => (
      <h4 className="text-lg font-bold my-1 text-gray-800" {...props}>{children}</h4>
    ),

    ul: ({ node, ...props }) => (
      <ul className="list-disc pl-5 my-2 space-y-1" {...props} />
    ),
    ol: ({ node, ...props }) => (
      <ol className="list-decimal pl-5 my-2 space-y-1" {...props} />
    ),
    li: ({ node, ...props }) => (
      <li className="text-gray-700" {...props} />
    ),
    blockquote: ({ node, ...props }) => (
      <blockquote className="border-l-4 border-gray-300 pl-4 italic my-4 text-gray-600" {...props} />
    ),
    // 我们在渲染前预处理 Markdown 内容，移除代码块和内联代码的特殊格式
  };

  // 自定义处理 pre 和 code 标签以及链接
  const transformMarkdown = (markdown) => {
    if (!markdown) return '';
    
    // 处理带括号的链接情况: (\[text\]\(url\))
    // 先将带括号的链接标记为特殊字符串，以便后续处理
    let processedMarkdown = markdown.replace(/\(\[(.*?)\]\((.*?)\)\)/g, (match, text, url) => {
      // 保留链接格式，去除外部括号
      return `[${text}](${url})`;
    });
    
    // 移除代码块 ```code``` 的特殊格式，保留内容
    const withoutCodeBlocks = processedMarkdown.replace(/```([\s\S]*?)```/g, '$1');
    
    // 移除内联代码 `code` 的特殊格式，保留内容
    return withoutCodeBlocks.replace(/`([^`]+)`/g, '$1').replace('```markdown', '').replace('diff','');
  };

  // Track what content has been rendered so far
  const [renderedContent, setRenderedContent] = useState('');
  const [isComplete, setIsComplete] = useState(false);
  const contentRef = useRef(content);
  const chunkRef = useRef(0);
  const renderTimerRef = useRef(null);
  
  // Reset rendering when content changes completely
  useEffect(() => {
    // Only reset if the content has significantly changed (not just appended to)
    if (!content.startsWith(contentRef.current) || content.length < contentRef.current.length) {
      setRenderedContent('');
      chunkRef.current = 0;
      setIsComplete(false);
    }
    contentRef.current = content;
    
    // Clear any existing render timer
    if (renderTimerRef.current) {
      clearTimeout(renderTimerRef.current);
    }
    
    // Start the incremental rendering process
    renderNextChunk();
    
    return () => {
      if (renderTimerRef.current) {
        clearTimeout(renderTimerRef.current);
      }
    };
  }, [content]);
  
  // Function to render the next chunk of content
  const renderNextChunk = () => {
    if (!content || chunkRef.current >= content.length) {
      setIsComplete(true);
      return;
    }
    
    // Calculate the end index for this chunk, ensuring we don't split in the middle of a markdown element
    const startIdx = chunkRef.current;
    let endIdx = Math.min(startIdx + chunkSize, content.length);
    
    // Try to find a good breaking point (newline) if we're not at the end
    if (endIdx < content.length) {
      // Look for the last newline in this chunk to avoid breaking in the middle of a line
      const lastNewline = content.lastIndexOf('\n', endIdx);
      if (lastNewline > startIdx) {
        endIdx = lastNewline + 1; // Include the newline
      }
    }
    
    // Update the rendered content with this chunk
    const newContent = content.substring(0, endIdx);
    setRenderedContent(newContent);
    
    // Update the chunk reference for next time
    chunkRef.current = endIdx;
    
    // Schedule the next chunk if we're not done
    if (endIdx < content.length) {
      renderTimerRef.current = setTimeout(renderNextChunk, renderDelay);
    } else {
      setIsComplete(true);
    }
  };
  
  // Apply transformations to the rendered content
  const transformedContent = useMemo(() => {
    return transformMarkdown(renderedContent);
  }, [renderedContent]);
  
  return (
    <div className="" id="markdown-content">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]} // 支持 GitHub Flavored Markdown（包括表格）
        components={components}
      >
        {transformedContent}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownRenderer;
